import { isPlatformBrowser } from '@angular/common'
import { Injectable, PLATFORM_ID, inject } from '@angular/core'

import { TranslateService } from '@ngx-translate/core'
import type { Observable } from 'rxjs'
import { switchMap, take, tap } from 'rxjs'

import { environment } from '../../environments/environment'
import { FeatureFlag } from '../../environments/environment.interface'
import { AuthService } from './auth/auth.service'
import { FeatureFlagService } from './feature-flag.service'

export const SUPPORTED_LANGUAGES = ['nl', 'en', 'qqx'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const LANGUAGE_STORAGE_KEY = 'preferred_language'

@Injectable({
  providedIn: 'root',
})
export class InternationalizationService {
  private readonly translateService = inject(TranslateService)
  private readonly featureFlagService = inject(FeatureFlagService)
  private readonly authService = inject(AuthService)

  private isBrowser: boolean
  private readonly originalFallbackLanguage: SupportedLanguage

  /**
   * This service is responsible for initializing the translationservice and
   * should be created before the app is loaded. It is also responsible for
   * storing the language preference. This might have to be split into two if we
   * ever start storing language serverside in the account, with one service
   * responsible for configuring translation service and another service
   * responsible for storing the language preference with dependency on auth.
   */
  constructor() {
    const platformId = inject(PLATFORM_ID)

    this.isBrowser = isPlatformBrowser(platformId)
    // TODO: make a better solution for this, getFallbackLang() returns string but can still be null at runtime when the language is not set yet. This is a workaround to make sure we always return a valid SupportedLanguage.
    this.originalFallbackLanguage = (this.translateService.getFallbackLang() ??
      'nl') as SupportedLanguage
  }

  /**
   * Sets an initial language before routing kicks in. The language route guards
   * (see language-route.guard.ts) are the actual source of truth once the
   * router has matched a URL, since the URL (nl vs /en) must win over any
   * stored/account preference for the page to render consistently on SSR.
   */
  public init(): void {
    const languagePreference: SupportedLanguage = this.getLanguagePreference()
    this.setActiveLanguage(
      languagePreference ?? this.originalFallbackLanguage
    ).subscribe()
  }

  /**
   * Applies the language dictated by the matched route (nl or en), without
   * persisting it as a preference. Used by the language route guards.
   */
  public applyRouteLanguage(
    languageKey: SupportedLanguage
  ): Observable<unknown> {
    return this.setActiveLanguage(languageKey)
  }

  /**
   * The visitor's explicitly stored/account language preference, independent of
   * whatever language the current URL is rendering. Used only to decide whether
   * to redirect a first-time nl visit to its /en equivalent.
   */
  public getPreferredLanguage(): SupportedLanguage | null {
    const enabledLanguages = this.enabledLanguages()

    if (this.isBrowser) {
      const storedLanguage = localStorage.getItem(
        LANGUAGE_STORAGE_KEY
      ) as SupportedLanguage
      if (storedLanguage && enabledLanguages.includes(storedLanguage)) {
        return storedLanguage
      }
    }

    const userLocale = this.authService.currentUser()?.locale as
      SupportedLanguage | undefined
    if (userLocale && enabledLanguages.includes(userLocale)) {
      return userLocale
    }

    return null
  }

  public setLanguagePreference(
    languageKey: SupportedLanguage
  ): Observable<unknown> {
    return this.setActiveLanguage(languageKey).pipe(
      tap(() => {
        if (this.isBrowser) {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, languageKey)
        }
      }),
      switchMap(() =>
        this.authService.updateUser({ locale: languageKey }).pipe(take(1))
      )
    )
  }

  public getLanguagePreference(): SupportedLanguage {
    // The active translation language is authoritative once routing has run,
    // since the language route guards set it to match the URL (nl vs /en).
    const currentLang =
      this.translateService.getCurrentLang() as SupportedLanguage | null
    if (currentLang && SUPPORTED_LANGUAGES.includes(currentLang)) {
      return currentLang
    }

    if (this.isBrowser) {
      const storedLanguage = localStorage.getItem(
        LANGUAGE_STORAGE_KEY
      ) as SupportedLanguage
      if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage)) {
        return storedLanguage
      }
    }

    // TODO: make a better solution for this, getCurrentLang() returns string but can still be undefined at runtime when the language is not set yet. This is a workaround to make sure we always return a valid SupportedLanguage.
    return (this.translateService.getFallbackLang() ??
      'nl') as SupportedLanguage
  }

  public enabledLanguages(): SupportedLanguage[] {
    return this.featureFlagService.isEnabled(FeatureFlag.QQX_LANGUAGE)
      ? [...SUPPORTED_LANGUAGES]
      : [...SUPPORTED_LANGUAGES.filter(language => language !== 'qqx')]
  }

  private setActiveLanguage(
    languageKey: SupportedLanguage
  ): Observable<unknown> {
    // setDefaultLang, otherwise qqx doesn't work

    const fallbackKey =
      languageKey.toLowerCase() === 'qqx' || environment.id !== 'production'
        ? languageKey.toLowerCase()
        : this.originalFallbackLanguage
    this.translateService.setFallbackLang(fallbackKey).subscribe()

    // .use also sets default language if there was no default language before
    return this.translateService.use(languageKey.toLowerCase())
  }
}
